import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Stack,
  Collapse,
  IconButton,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  AutoAwesome as GenerateIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  SmartToy as PromptIcon,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import { personaService } from '../services/api';
import type { Persona, GeneratePersonaRequest } from '@personawiz/shared';
import PersonaCard from '../components/persona/PersonaCard';
import {
  personaToMarkdown,
  personaToSystemPrompt,
  copyToClipboard,
  downloadPersonaJSON,
} from '../utils/personaExport';

const examplePersonas: Array<{ userRole: string; useCase: string; label: string }> = [
  {
    userRole: 'Product Manager',
    useCase: 'Analyzing user feedback for roadmap prioritization',
    label: 'PM Feedback Analysis',
  },
  {
    userRole: 'Junior Developer',
    useCase: 'Learning to debug complex async code',
    label: 'Dev Learning Journey',
  },
  {
    userRole: 'Marketing Manager',
    useCase: 'Creating data-driven campaign reports',
    label: 'Marketing Analytics',
  },
  {
    userRole: 'Customer Support Lead',
    useCase: 'Building self-service knowledge base',
    label: 'Support Automation',
  },
];

interface LocationState {
  projectId?: string;
  projectName?: string;
}

export default function PersonaGenerator() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  
  // Check if we're generating for a specific project
  const projectId = state?.projectId;
  const projectName = state?.projectName;
  
  const [userRole, setUserRole] = useState('');
  const [useCase, setUseCase] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generatedPersona, setGeneratedPersona] = useState<Persona | null>(null);
  
  const [recentPersonas, setRecentPersonas] = useState<Persona[]>([]);
  const [showRecent, setShowRecent] = useState(true);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    personaService.list({ pageSize: 6 })
      .then((res) => setRecentPersonas(res.data))
      .catch(() => {}); // Silently fail for recent list
  }, [generatedPersona]);

  const handleGenerate = async () => {
    if (!userRole.trim() || !useCase.trim()) {
      setError('Please fill in both the User Role and Use Case fields.');
      return;
    }

    setError('');
    setIsGenerating(true);
    setGeneratedPersona(null);

    try {
      const request: GeneratePersonaRequest = {
        userRole: userRole.trim(),
        useCase: useCase.trim(),
        ...(projectId && { projectId }),
      };
      const persona = await personaService.generate(request);
      setGeneratedPersona(persona);
      
      // If generating for a project, navigate back to the project page
      if (projectId) {
        setSnackbar(`Persona added to project "${projectName}"`);
        setTimeout(() => navigate(`/projects/${projectId}`), 1500);
      }
    } catch (err: unknown) {
      // 401s are handled centrally by api.ts 401 interceptor (calls logout()).
      const errorMsg = err instanceof Error ? err.message : 'Failed to generate persona. Please try again.';
      setError(errorMsg);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExampleClick = (example: { userRole: string; useCase: string }) => {
    setUserRole(example.userRole);
    setUseCase(example.useCase);
    setGeneratedPersona(null);
    setError('');
  };

  const handleReset = () => {
    setUserRole('');
    setUseCase('');
    setGeneratedPersona(null);
    setError('');
  };

  return (
    <Box>
      {/* Project Context Banner */}
      {projectId && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate(`/projects/${projectId}`)}>
              Back to Project
            </Button>
          }
        >
          Generating persona for project: <strong>{projectName}</strong>
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Persona Generator
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Generate a functional persona by describing a user role and their specific use case.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Input Section */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Describe Your User
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="User Role"
                placeholder="e.g., Product Manager, Junior Developer, Marketing Lead"
                value={userRole}
                onChange={(e) => setUserRole(e.target.value)}
                sx={{ mb: 3 }}
                disabled={isGenerating}
              />

              <TextField
                fullWidth
                label="Use Case"
                placeholder="e.g., Analyzing user feedback for roadmap prioritization"
                value={useCase}
                onChange={(e) => setUseCase(e.target.value)}
                multiline
                rows={3}
                sx={{ mb: 3 }}
                disabled={isGenerating}
              />

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <GenerateIcon />}
                  onClick={handleGenerate}
                  disabled={isGenerating || !userRole.trim() || !useCase.trim()}
                  sx={{ flex: 1 }}
                >
                  {isGenerating ? 'Generating...' : 'Generate Persona'}
                </Button>
                {(userRole || useCase) && (
                  <Button variant="outlined" onClick={handleReset} disabled={isGenerating}>
                    Reset
                  </Button>
                )}
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Try an example:
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {examplePersonas.map((example) => (
                  <Chip
                    key={example.label}
                    label={example.label}
                    onClick={() => handleExampleClick(example)}
                    variant="outlined"
                    disabled={isGenerating}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>

          {/* Recent Personas */}
          {recentPersonas.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowRecent(!showRecent)}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Recent Personas ({recentPersonas.length})
                  </Typography>
                  <IconButton size="small">
                    {showRecent ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={showRecent}>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      {recentPersonas.map((persona) => (
                        <Card
                          key={persona.id}
                          variant="outlined"
                          sx={{ cursor: 'pointer' }}
                        >
                          <CardActionArea onClick={() => navigate(`/personas/${persona.id}`)}>
                            <CardContent sx={{ py: 1.5, px: 2 }}>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {persona.coreIdentity.personaName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {persona.coreIdentity.userRole} • {persona.coreIdentity.useCase}
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      ))}
                    </Stack>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Output Section */}
        <Grid item xs={12} lg={6}>
          {isGenerating ? (
            <Card sx={{ height: '100%', minHeight: 400 }}>
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: 400,
                }}
              >
                <CircularProgress size={48} sx={{ mb: 3 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Generating Your Persona
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Our AI is crafting a detailed functional persona based on your inputs.
                  <br />
                  This usually takes 10-20 seconds.
                </Typography>
              </CardContent>
            </Card>
          ) : generatedPersona ? (
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 2, justifyContent: 'flex-end' }}>
                <Tooltip title="Copy as Markdown">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={async () => {
                      const ok = await copyToClipboard(personaToMarkdown(generatedPersona));
                      setSnackbar(ok ? 'Markdown copied!' : 'Failed to copy');
                    }}
                  >
                    Markdown
                  </Button>
                </Tooltip>
                <Tooltip title="Copy as AI System Prompt">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PromptIcon />}
                    onClick={async () => {
                      const ok = await copyToClipboard(personaToSystemPrompt(generatedPersona));
                      setSnackbar(ok ? 'System prompt copied!' : 'Failed to copy');
                    }}
                  >
                    System Prompt
                  </Button>
                </Tooltip>
                <Tooltip title="Download JSON">
                  <IconButton
                    size="small"
                    onClick={() => {
                      downloadPersonaJSON(generatedPersona);
                      setSnackbar('JSON downloaded!');
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              <PersonaCard persona={generatedPersona} expanded />
            </Box>
          ) : (
            <Card sx={{ height: '100%', minHeight: 400 }}>
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: 400,
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    opacity: 0.1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <GenerateIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 1 }} />
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Your Persona Will Appear Here
                </Typography>
                <Typography variant="body2" color="text.secondary" maxWidth={300}>
                  Fill in the user role and use case on the left, then click Generate to create a functional persona.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
      
      {/* Success Snackbar */}
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={3000}
        onClose={() => setSnackbar('')}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
